/**
 * @fileoverview Per-user collapse state for variation stacks.
 *
 * Controls whether stacks are visually expanded (showing individual nodes)
 * or collapsed (showing a single StackNode). Persisted to localStorage
 * so each user retains their own view preference.
 *
 * Shared between canvas and sidebar for consistent behavior.
 */

import { create } from 'zustand'

const STORAGE_KEY = 'martini-variation-stack-expanded'

/** Default: stacks with this many+ items auto-collapse. */
const AUTO_COLLAPSE_THRESHOLD = 2

function loadExpandedIds(): Set<string> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      return new Set(JSON.parse(stored) as string[])
    }
  } catch {
    // Ignore parse errors
  }
  return new Set()
}

function saveExpandedIds(ids: Set<string>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...ids]))
  } catch {
    // Ignore storage errors
  }
}

interface VariationStackState {
  /** Stack IDs that the user has explicitly expanded. */
  expandedStackIds: Set<string>

  /**
   * Check if a stack should be displayed as expanded.
   * Returns true if the user explicitly expanded it, or if itemCount is below threshold.
   */
  isExpanded: (stackId: string, itemCount: number) => boolean

  /** Toggle a stack between expanded and collapsed. */
  toggleStack: (stackId: string) => void

  /** Expand all stacks. */
  expandAll: (stackIds: string[]) => void

  /** Collapse all stacks (clear expanded set). */
  collapseAll: () => void

  /** Replace the entire expanded set (used by undo/redo to restore state). */
  setExpandedStackIds: (ids: Set<string>) => void

  /** Ephemeral per-stack carousel index (not persisted). */
  activeIndexByStack: Record<string, number>

  /** Get the active carousel index for a stack (defaults to 0). */
  getActiveIndex: (stackId: string) => number

  /** Set the active carousel index for a stack. */
  setActiveIndex: (stackId: string, index: number) => void

  /** Cycle the active index by delta (+1 / -1), wrapping around. */
  cycleActiveIndex: (stackId: string, delta: number, memberCount: number) => void

  /**
   * Stack IDs whose visible top item is currently the selected target —
   * the user clicked on the front thumbnail of a closed stack and is
   * operating on the active member instead of the stack as a whole. The
   * StackNodeView paints its selection ring around the thumbnail
   * rectangle (mirrors the open-stack member-selection look) and
   * keyboard shortcuts route to the active asset rather than the stack.
   *
   * Distinct from the engine's `selectionManager` selection of the
   * stack node itself — both can be set at once (shift+click), and a
   * plain click on the thumbnail clears the engine stack selection.
   */
  topItemSelectedStacks: Set<string>

  setTopItemSelected: (stackId: string, selected: boolean) => void
  toggleTopItemSelected: (stackId: string) => void
  clearTopItemSelected: () => void
}

export const useVariationStackStore = create<VariationStackState>((set, get) => ({
  expandedStackIds: loadExpandedIds(),

  isExpanded: (stackId: string, itemCount: number) => {
    // Stacks below the threshold are always "expanded" (shown as individual nodes)
    if (itemCount < AUTO_COLLAPSE_THRESHOLD) return true
    return get().expandedStackIds.has(stackId)
  },

  toggleStack: (stackId: string) => {
    set(state => {
      const next = new Set(state.expandedStackIds)
      if (next.has(stackId)) {
        next.delete(stackId)
      } else {
        next.add(stackId)
      }
      saveExpandedIds(next)
      return { expandedStackIds: next }
    })
  },

  expandAll: (stackIds: string[]) => {
    set(() => {
      const next = new Set(stackIds)
      saveExpandedIds(next)
      return { expandedStackIds: next }
    })
  },

  collapseAll: () => {
    set(() => {
      const next = new Set<string>()
      saveExpandedIds(next)
      return { expandedStackIds: next }
    })
  },

  setExpandedStackIds: (ids: Set<string>) => {
    set(() => {
      saveExpandedIds(ids)
      return { expandedStackIds: ids }
    })
  },

  activeIndexByStack: {},

  getActiveIndex: (stackId: string) => {
    return get().activeIndexByStack[stackId] ?? 0
  },

  setActiveIndex: (stackId: string, index: number) => {
    set(state => ({
      activeIndexByStack: { ...state.activeIndexByStack, [stackId]: index },
    }))
  },

  cycleActiveIndex: (stackId: string, delta: number, memberCount: number) => {
    if (memberCount <= 0) return
    set(state => {
      const current = state.activeIndexByStack[stackId] ?? 0
      const next = (((current + delta) % memberCount) + memberCount) % memberCount
      return { activeIndexByStack: { ...state.activeIndexByStack, [stackId]: next } }
    })
  },

  topItemSelectedStacks: new Set<string>(),

  setTopItemSelected: (stackId: string, selected: boolean) => {
    set(state => {
      const isCurrentlySelected = state.topItemSelectedStacks.has(stackId)
      if (selected === isCurrentlySelected) return state
      const next = new Set(state.topItemSelectedStacks)
      if (selected) {
        next.add(stackId)
      } else {
        next.delete(stackId)
      }
      return { topItemSelectedStacks: next }
    })
  },

  toggleTopItemSelected: (stackId: string) => {
    set(state => {
      const next = new Set(state.topItemSelectedStacks)
      if (next.has(stackId)) {
        next.delete(stackId)
      } else {
        next.add(stackId)
      }
      return { topItemSelectedStacks: next }
    })
  },

  clearTopItemSelected: () => {
    set(state => (state.topItemSelectedStacks.size === 0 ? state : { topItemSelectedStacks: new Set<string>() }))
  },
}))
